import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AnalyticsConsent,
  getAnalyticsConsent,
  subscribeAnalyticsConsent,
} from '../utils/analyticsConsent';
import { getAnalyticsRoute, isPageViewAnalyticsAllowed } from './routePrivacy';

export const GOOGLE_ANALYTICS_MEASUREMENT_ID = 'G-NMTR7MV5Q5';

const CANONICAL_ORIGIN = 'https://www.tcgcalendar.com.au';
const FRAME_ID = 'tcg-calendar-google-analytics-frame';
const SCRIPT_ID = 'tcg-calendar-google-analytics';

type GoogleConsentState = 'granted' | 'denied';
type GtagArguments = IArguments | unknown[];
type GoogleAnalyticsPageView = ReturnType<typeof buildGoogleAnalyticsPageView>;

type GoogleAnalyticsWindow = Window & {
  dataLayer?: GtagArguments[];
  gtag?: (...args: unknown[]) => void;
};

let analyticsFrame: HTMLIFrameElement | null = null;
let frameReady = false;
let loadScheduled = false;
let latestConsent: AnalyticsConsent = 'unknown';
let latestPageView: GoogleAnalyticsPageView = null;

export const getGoogleAnalyticsConsentState = (
  consent: AnalyticsConsent,
): GoogleConsentState => (consent === 'granted' ? 'granted' : 'denied');

export const buildGoogleAnalyticsPageView = (pathname: string) => {
  const route = getAnalyticsRoute(pathname);
  if (!route) return null;

  return {
    page_location: `${CANONICAL_ORIGIN}${route}`,
    page_path: route,
    page_title: route,
    page_referrer: '',
  };
};

export const createGoogleAnalyticsFrame = () => {
  const iframe = document.createElement('iframe');
  iframe.id = FRAME_ID;
  iframe.title = 'Google Analytics';
  iframe.hidden = true;
  iframe.tabIndex = -1;
  iframe.setAttribute('aria-hidden', 'true');
  iframe.referrerPolicy = 'no-referrer';
  iframe.src = '/ga-runtime-frame.html';
  return iframe;
};

const ensureQueue = (target: GoogleAnalyticsWindow) => {
  target.dataLayer = target.dataLayer || [];
  if (!target.gtag) {
    target.gtag = function gtag() {
      target.dataLayer?.push(arguments);
    };
  }
  return target.gtag;
};

const consentPayload = (consent: AnalyticsConsent) => ({
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: getGoogleAnalyticsConsentState(consent),
});

const appendScript = (frameDocument: Document) => {
  if (frameDocument.getElementById(SCRIPT_ID)) return;

  const script = frameDocument.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.setAttribute('fetchpriority', 'low');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_MEASUREMENT_ID}`;
  frameDocument.body.appendChild(script);
};

const sendPageViewToFrame = (pageView: GoogleAnalyticsPageView) => {
  if (!pageView || !frameReady || !analyticsFrame?.contentWindow) return;
  const gtag = ensureQueue(analyticsFrame.contentWindow as GoogleAnalyticsWindow);
  gtag('set', pageView);
  gtag('event', 'page_view', pageView);
};

const initialiseFrame = (iframe: HTMLIFrameElement) => {
  const target = iframe.contentWindow as GoogleAnalyticsWindow | null;
  const frameDocument = iframe.contentDocument;
  if (!target || !frameDocument || !latestPageView) return;

  const gtag = ensureQueue(target);
  gtag('consent', 'default', consentPayload(latestConsent));
  gtag('set', {
    ads_data_redaction: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...latestPageView,
  });
  gtag('js', new Date());
  gtag('config', GOOGLE_ANALYTICS_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  frameReady = true;
  sendPageViewToFrame(latestPageView);
  appendScript(frameDocument);
};

const mountFrame = () => {
  if (analyticsFrame || !latestPageView) return;

  const iframe = createGoogleAnalyticsFrame();
  analyticsFrame = iframe;
  iframe.addEventListener('load', () => initialiseFrame(iframe), { once: true });
  document.body.appendChild(iframe);
};

const scheduleFrameAfterPageLoad = () => {
  if (loadScheduled) return;
  loadScheduled = true;

  const scheduleDuringIdle = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(mountFrame, { timeout: 4000 });
      return;
    }
    window.setTimeout(mountFrame, 1500);
  };

  if (document.readyState === 'complete') {
    scheduleDuringIdle();
  } else {
    window.addEventListener('load', scheduleDuringIdle, { once: true });
  }
};

const initialise = (consent: AnalyticsConsent, pageView: GoogleAnalyticsPageView) => {
  if (!pageView) return;
  latestConsent = consent;
  latestPageView = pageView;
  scheduleFrameAfterPageLoad();
};

const updateConsent = (consent: AnalyticsConsent) => {
  latestConsent = consent;
  if (!frameReady || !analyticsFrame?.contentWindow) return;
  ensureQueue(analyticsFrame.contentWindow as GoogleAnalyticsWindow)(
    'consent',
    'update',
    consentPayload(consent),
  );
};

const sendPageView = (pageView: GoogleAnalyticsPageView) => {
  if (!pageView) return;
  latestPageView = pageView;
  sendPageViewToFrame(pageView);
};

const GoogleAnalyticsRuntime: React.FC = () => {
  const location = useLocation();
  const pageView = useMemo(
    () => buildGoogleAnalyticsPageView(location.pathname),
    [location.pathname],
  );
  const enabled = Boolean(pageView && typeof window !== 'undefined' && isPageViewAnalyticsAllowed(
    window.location.hostname,
    process.env.NODE_ENV === 'production',
    location.pathname,
  ));

  useEffect(() => {
    if (!enabled || !pageView) return undefined;

    initialise(getAnalyticsConsent(), pageView);
    return subscribeAnalyticsConsent((consent) => updateConsent(consent));
  }, [enabled, pageView]);

  useEffect(() => {
    if (!enabled || !pageView) return;
    initialise(getAnalyticsConsent(), pageView);
    sendPageView(pageView);
  }, [enabled, pageView]);

  return null;
};

export default GoogleAnalyticsRuntime;
